import { registerBlockType } from '@wordpress/blocks';
import { __ } from '@wordpress/i18n';
import { useWooBlockProps } from '@woocommerce/block-templates';
import {
	__experimentalUseProductEntityProp as useProductEntityProp,
} from '@woocommerce/product-editor';

import { checkConditionalLogic } from '../../utils/ConditionalLogicChecker';
import { fieldComponents, defaultValues } from '../../components/fieldComponents';

/**
 * Renders a dynamic field component based on field type and WooCommerce product meta data.
 *
 * @param {Object}   props
 * @param {Object}   props.field     - Field configuration object.
 * @param {Object}   props.context   - Context object passed from block editor.
 */
const DynamicField = ({ field, context }) => {
	// Bind the meta field to WooCommerce product entity.
	const [value, setValue] = useProductEntityProp(`meta_data.${field.field_key}`, {
		postType: context.postType,
		fallbackValue:
			typeof field.default_value !== 'undefined'
				? field.default_value
				: defaultValues[field.type] || '',
	});

	// Choose the correct React component for the field type.
	const FieldComponent = fieldComponents[field.type] || fieldComponents.text;

	if (!FieldComponent) {
		return <p>{__('Unsupported field type:', 'voxycure-framework')} "{field.type}"</p>;
	}

	return (
		<>
			<div style={{ marginBottom: '1rem' }}>
				<FieldComponent
					field={field}
					value={value}
					onChange={setValue}
				/>
			</div>
		</>
	);
};

/**
 * Edit component for rendering the block in editor.
 *
 * @param {Object}   props
 * @param {Object}   props.attributes - Block attributes.
 * @param {Object}   props.context    - Context for the block.
 * @param {string}   props.name       - Block name (e.g., voxycure/example).
 */
const Edit = ({ attributes, context, name }) => {
	const blockProps = useWooBlockProps(attributes);
	const blockKey = name.split('/')[1]; // Extract 'example' from 'voxycure/example'
	const blockData = window.voxycureWCBlocks?.[blockKey];

	if (!blockData || !Array.isArray(blockData.fields)) {
		return (
			<div {...blockProps}>
				<p>
					{__('⚠️ No dynamic fields found for block:', 'voxycure-framework')}{' '}
					<strong>{name}</strong>
				</p>
				<p>{__('Please check your Voxycure Framework configuration.', 'voxycure-framework')}</p>
			</div>
		);
	}

	// Build dynamic meta object
	const productMeta = {};
	blockData.fields.forEach((field) => {
		const [value] = useProductEntityProp(`meta_data.${field.field_key}`, {
			postType: context.postType,
			fallbackValue: field.default_value ?? '',
		});
		productMeta[field.field_key] = value;
	});

	return (
		<div {...blockProps}>
			{blockData.fields
				.filter((field) =>
					checkConditionalLogic(productMeta, field.conditional_logic)
				)
				.map((field) => (
					<DynamicField
						key={field.field_key || field.label || field.name}
						field={field}
						context={context}
					/>
				))}
		</div>
	);
};

// Loop through all custom block definitions and register them.
const blocksToRegister = window.voxycureWCBlocks || {};

Object.entries(blocksToRegister).forEach(([blockKey, blockData]) => {
	registerBlockType(`voxycure/${blockKey}`, {
		title: blockData.title
			? __(blockData.title, 'voxycure-framework')
			: __('Voxycure Block', 'voxycure-framework'),
		icon: 'layout',
		category: 'woocommerce',
		apiVersion: 3,
		edit: Edit,
		save: () => null, // Dynamic blocks don’t need a static save function
	});
});
